import React, { useState } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { ShoppingCart, Trash2, MessageSquare } from 'lucide-react';
import { Badge } from './ui/badge';

const Cart = ({ items, onRemove, onUpdateQuantity, onCheckout }) => {
  const [clientNote, setClientNote] = useState('');
  const total = items.reduce((sum, item) => sum + (item.is_quote_only ? 0 : item.price) * item.quantity, 0);
  const hasQuoteOnly = items.some((it) => it.is_quote_only);

  const handleCheckoutClick = () => {
    onCheckout(clientNote);
    setClientNote('');
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          className="fixed bottom-8 right-8 bg-orange-600 hover:bg-orange-700 text-white rounded-full w-16 h-16 shadow-lg z-50"
          data-testid="cart-trigger-button"
        >
          <ShoppingCart className="w-6 h-6" />
          {items.length > 0 && (
            <Badge className="absolute -top-1 -right-1 bg-green-600">{items.length}</Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md flex flex-col h-full p-0">
        <SheetHeader className="p-6 border-b border-slate-200 shrink-0">
          <SheetTitle style={{ fontFamily: 'Outfit' }}>Coș de cumpărături</SheetTitle>
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          {items.length === 0 ? (
            <p className="text-slate-500 text-center py-8" style={{ fontFamily: 'IBM Plex Sans' }}>
              Coșul tău este gol
            </p>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center space-x-4 p-4 bg-slate-50 rounded-lg border border-slate-200"
                  data-testid={`cart-item-${item.id}`}
                >
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                      {item.name}
                    </h4>
                    {item.is_quote_only ? (
                      <p className="text-sm font-semibold text-orange-600" style={{ fontFamily: 'IBM Plex Sans' }}>
                        Cere ofertă · Cantitate {item.quantity}
                      </p>
                    ) : (
                      <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>
                        {item.price} RON x {item.quantity}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                      data-testid={`decrease-quantity-${item.id}`}
                    >
                      -
                    </Button>
                    <span className="w-8 text-center font-semibold">{item.quantity}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                      data-testid={`increase-quantity-${item.id}`}
                    >
                      +
                    </Button>
                  </div>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onRemove(item.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`remove-item-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}

              <div className="pt-4">
                <Label
                  htmlFor="client-note"
                  className="flex items-center space-x-2 mb-2 text-slate-700"
                  style={{ fontFamily: 'IBM Plex Sans' }}
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>Instrucțiuni speciale (opțional)</span>
                </Label>
                <Textarea
                  id="client-note"
                  value={clientNote}
                  onChange={(e) => setClientNote(e.target.value)}
                  placeholder="Ex: livrare după ora 14:00, sunați înainte, etc."
                  rows={3}
                  className="resize-none"
                  data-testid="cart-client-note-input"
                />
              </div>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-slate-200 p-6 shrink-0 bg-white">
            <div className="flex justify-between items-center mb-2">
              <span className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                Total:
              </span>
              <span
                className="text-2xl font-bold text-blue-900"
                style={{ fontFamily: 'Outfit' }}
                data-testid="cart-total"
              >
                {total.toFixed(2)} RON
              </span>
            </div>

            {hasQuoteOnly && (
              <p className="text-xs text-orange-700 mb-3" style={{ fontFamily: 'IBM Plex Sans' }}>
                Produsele „Cere ofertă" nu sunt incluse în total. Prețul va fi confirmat separat.
              </p>
            )}

            <Button
              onClick={handleCheckoutClick}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              data-testid="checkout-button"
              style={{ fontFamily: 'IBM Plex Sans' }}
            >
              Trimite Comanda
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default Cart;
